/**
 * Combat: swings by weapon, blows landing, the guard, and the three sounds of the combat triangle
 * — the parry clang (block beats attack), the shatter of a broken guard (break beats block), the
 * snap of an interrupted wind-up (attack beats break) — and the 破 wind-up warning that tells the
 * player a break is coming. Each must read on its own, with the screen shaking or not.
 */
import { BELL, STEEL } from '../kit';
import { registerSfx } from '../registry';
import { drum, gong, note, ring, ROOT_D, whoosh } from '../voices';

// ───────── swings ─────────

/** Weapon families that swing differently. */
export const SWING_WEAPONS = ['sword', 'sabre', 'fist', 'staff'] as const;
export type SwingWeapon = (typeof SWING_WEAPONS)[number];

const WEAPON_LABEL: Record<SwingWeapon, string> = {
  sword: 'กระบี่',
  sabre: 'ดาบ',
  fist: 'หมัด · ฝ่ามือ',
  staff: 'กระบอง · ทวน',
};

for (const w of SWING_WEAPONS)
  for (const heavy of [false, true])
    registerSfx(
      `combat.swing.${w}.${heavy ? 'heavy' : 'light'}`,
      { label: `ฟัน${WEAPON_LABEL[w]} (${heavy ? 'หนัก' : 'เบา'})`, cat: 'combat', vary: 0.07, gap: 0.04 },
      (k) => {
        const h = heavy ? 1 : 0;
        if (w === 'sword') {
          // A jian: thin and keen, a high whistle with a glint of steel.
          whoosh(k, { dur: 0.15 + h * 0.08, f0: 900, f1: 4200, f2: 2200, vol: 0.32 + h * 0.08, q: 3.2 });
          k.tone({ f: 5200, f1: 4400, at: 0.05, dur: 0.1, vol: 0.025 });
          if (heavy) whoosh(k, { dur: 0.26, f0: 300, f1: 900, f2: 400, vol: 0.18, q: 1.2, color: 'pink' });
        } else if (w === 'sabre') {
          // A dao: broad and heavy, more body than whistle.
          whoosh(k, { dur: 0.2 + h * 0.08, f0: 380, f1: 2100, f2: 900, vol: 0.34 + h * 0.06, q: 1.6 });
          whoosh(k, {
            dur: 0.22 + h * 0.1,
            f0: 160,
            f1: 520,
            f2: 220,
            vol: 0.2 + h * 0.08,
            q: 1,
            color: 'pink',
          });
          if (heavy) k.tone({ f: 3300, f1: 2900, at: 0.08, dur: 0.12, vol: 0.02 });
        } else if (w === 'fist') {
          // Bare hands: sleeves snapping and a push of air; heavy is a palm full of qi.
          k.noise({ dur: 0.06, vol: 0.2, attack: 0.004, type: 'highpass', f: 1800, q: 0.8 });
          whoosh(k, { dur: 0.12 + h * 0.06, f0: 500, f1: 1600, f2: 700, vol: 0.2, q: 1.4 });
          if (heavy) {
            k.thump({ f0: 140, f1: 55, at: 0.05, dur: 0.22, vol: 0.28 });
            k.noise({
              color: 'brown',
              at: 0.04,
              dur: 0.25,
              vol: 0.25,
              attack: 0.03,
              type: 'lowpass',
              f: 500,
            });
          }
        } else {
          // A staff or spear: a deep whoom, turning (heavy spins it twice).
          whoosh(k, {
            dur: 0.3 + h * 0.12,
            f0: 180,
            f1: 760,
            f2: 240,
            vol: 0.36,
            q: 2.2,
            color: 'pink',
          });
          k.noise({
            dur: 0.3 + h * 0.12,
            vol: 0.1,
            attack: 0.12,
            f: 1100,
            q: 2,
            trem: [heavy ? 16 : 10, 0.8],
          });
        }
      },
    );

registerSfx(
  'combat.swing.claw',
  { label: 'ตะปบ · งับ (สัตว์)', cat: 'combat', vary: 0.1, gap: 0.05 },
  (k) => {
    whoosh(k, { dur: 0.14, f0: 600, f1: 2400, f2: 1200, vol: 0.26, q: 1.8 });
    // A growl under it.
    const g = k.filter('lowpass', 520, 2);
    k.tone({ type: 'sawtooth', f: 95, f1: 70, dur: 0.28, vol: 0.12, attack: 0.02, vib: [24, 0.06], out: g });
  },
);

registerSfx('combat.whiff', { label: 'ฟันพลาด', cat: 'combat', vary: 0.08, gap: 0.08 }, (k) => {
  whoosh(k, { dur: 0.18, f0: 700, f1: 1500, f2: 500, vol: 0.12, q: 1.5 });
});

registerSfx('combat.dodge', { label: 'หลบ', cat: 'combat', vary: 0.08, gap: 0.06 }, (k) => {
  whoosh(k, { dur: 0.16, f0: 1600, f1: 5200, f2: 2400, vol: 0.2, q: 2.5, peak: 0.3 });
  k.noise({ at: 0.02, dur: 0.08, vol: 0.06, type: 'highpass', f: 5000 });
});

registerSfx('combat.spin', { label: 'หมุนวงดาบ', cat: 'combat', vary: 0.05, gap: 0.08 }, (k) => {
  whoosh(k, { dur: 0.42, f0: 400, f1: 2400, f2: 700, vol: 0.3, q: 1.8, peak: 0.5 });
  k.noise({ dur: 0.42, vol: 0.14, attack: 0.1, f: 1500, q: 2, trem: [14, 0.9] });
});

// ───────── blows landing ─────────

registerSfx('combat.hit.slash', { label: 'คมดาบเข้าเนื้อ', cat: 'combat', vary: 0.08, gap: 0.03 }, (k) => {
  // The cut: a bright tearing burst; the body: a short thud; and a ring of the edge.
  k.noise({ dur: 0.1, vol: 0.34, attack: 0.002, type: 'highpass', f: 2600, f1: 1400, q: 0.9 });
  k.noise({ dur: 0.14, vol: 0.22, attack: 0.002, f: 900, f1: 300, q: 1.2 });
  k.thump({ f0: 170, f1: 70, dur: 0.14, vol: 0.32 * k.i });
  k.tone({ f: 2100, f1: 2050, at: 0.005, dur: 0.12, vol: 0.03 });
});

registerSfx('combat.hit.blunt', { label: 'กระแทกทื่อ', cat: 'combat', vary: 0.08, gap: 0.03 }, (k) => {
  k.thump({ f0: 130, f1: 45, dur: 0.2, vol: 0.42 * k.i });
  k.noise({ color: 'brown', dur: 0.14, vol: 0.4, attack: 0.002, type: 'lowpass', f: 900, f1: 250 });
  k.click({ f: 2400, vol: 0.12 });
});

registerSfx('combat.hit.crit', { label: 'ติดคริติคอล', cat: 'combat', vary: 0.05, gap: 0.03 }, (k) => {
  // Everything at once: the cut, a deep boom, a crunch, and the blade singing high after.
  const crunch = k.shaper(6, k.gain(0.5));
  k.noise({ dur: 0.12, vol: 0.4, attack: 0.002, type: 'highpass', f: 3000, f1: 1200, out: crunch });
  k.thump({ f0: 110, f1: 32, dur: 0.38, vol: 0.5 });
  k.noise({ color: 'brown', dur: 0.3, vol: 0.4, attack: 0.002, type: 'lowpass', f: 1400, f1: 120 });
  k.metal({ f: 2800, at: 0.02, dur: 0.6, vol: 0.06 });
  k.tone({ f: 4200, f1: 6400, at: 0.03, dur: 0.18, vol: 0.03 });
});

registerSfx('combat.hurt', { label: 'ผู้เล่นโดนตี', cat: 'combat', vary: 0.06, gap: 0.12 }, (k) => {
  // The player's own pain: heavier and lower than a hit dealt, with a dull ringing in the ears.
  k.thump({ f0: 160, f1: 50, dur: 0.22, vol: 0.38 });
  const lp = k.filter('lowpass', 800, 1.2);
  k.tone({ type: 'sawtooth', f: 190, f1: 75, dur: 0.18, vol: 0.12, out: lp });
  k.noise({ dur: 0.14, vol: 0.18, f: 900, f1: 300, q: 1 });
  k.tone({ f: 1250, at: 0.03, dur: 0.35, vol: 0.012, attack: 0.03 });
});

// ───────── the guard and the triangle ─────────

registerSfx('combat.guard', { label: 'ตั้งการ์ด', cat: 'combat', vary: 0.06, gap: 0.2 }, (k) => {
  k.noise({ dur: 0.08, vol: 0.12, attack: 0.01, f: 1200, f1: 600, q: 1.2 });
  k.metal({ f: 1600, at: 0.02, dur: 0.18, vol: 0.025 });
});

registerSfx('combat.block', { label: 'รับการ์ด (ปะทะ)', cat: 'combat', vary: 0.05, gap: 0.04 }, (k) => {
  // Blade on blade: a hard click, then the clang ringing out.
  k.noise({ dur: 0.03, vol: 0.32, attack: 0.001, type: 'highpass', f: 3200 });
  k.metal({ f: 1250, dur: 0.55, vol: 0.13 });
  k.metal({ f: 1870, dur: 0.3, vol: 0.06 });
  k.thump({ f0: 220, f1: 110, dur: 0.08, vol: 0.18 });
});

registerSfx(
  'combat.parry',
  { label: 'ปัดป้อง (เหล็กกระทบเหล็ก)', cat: 'combat', vary: 0.04, gap: 0.04 },
  (k) => {
    // A brighter clash with a sliding scrape of edges and a second ring.
    k.noise({ dur: 0.025, vol: 0.34, attack: 0.001, type: 'highpass', f: 3600 });
    k.metal({ f: 1650, dur: 0.7, vol: 0.13 });
    k.noise({ at: 0.015, dur: 0.18, vol: 0.12, attack: 0.01, f: 5200, f1: 2800, q: 6 });
    k.metal({ f: 2350, at: 0.06, dur: 0.4, vol: 0.05 });
  },
);

registerSfx('combat.guardBreak', { label: 'การ์ดแตก (破)', cat: 'combat', vary: 0.03, gap: 0.08 }, (k) => {
  // The qi shield shattering: glassy crackle, a cracked low clang, a crunching drop.
  const crunch = k.shaper(8, k.gain(0.45));
  k.noise({ color: 'crackle', dur: 0.45, vol: 0.9, attack: 0.002, type: 'highpass', f: 2200, q: 0.6 });
  k.noise({ dur: 0.35, vol: 0.32, attack: 0.002, f: 3000, f1: 400, q: 0.9, out: crunch });
  k.metal({ f: 690, dur: 0.6, vol: 0.12, partials: STEEL });
  k.metal({ f: 733, dur: 0.5, vol: 0.08 });
  k.tone({ type: 'sawtooth', f: 280, f1: 40, dur: 0.32, vol: 0.12, out: crunch });
  k.thump({ f0: 100, f1: 35, dur: 0.4, vol: 0.4 });
  for (let n = 0; n < 6; n++) k.tone({ f: k.range(3000, 6500), at: 0.02 + n * 0.035, dur: 0.06, vol: 0.03 });
});

registerSfx('combat.interrupt', { label: 'ขัดจังหวะ', cat: 'combat', vary: 0.04, gap: 0.08 }, (k) => {
  // A wind-up snapped off: a sharp tick, a falling cut-off whine, a dry thud.
  k.noise({ dur: 0.02, vol: 0.34, attack: 0.001, type: 'highpass', f: 4000 });
  k.metal({ f: 1800, dur: 0.25, vol: 0.07 });
  k.tone({ type: 'triangle', f: 1500, f1: 380, dur: 0.18, vol: 0.12 });
  k.thump({ f0: 180, f1: 80, at: 0.01, dur: 0.12, vol: 0.25 });
});

registerSfx(
  'combat.breakWindup',
  { label: 'สัญญาณท่าตัดการ์ด (破) กำลังมา', cat: 'combat', vary: 0.01, gap: 0.15, preview: 1.4 },
  (k, o) => {
    // The warning: two clashing bell strikes (a tritone, unlike any other cue), then a metallic
    // scrape rising to the moment the break lands.
    const len = Math.max(0.4, Math.min(2, o.len ?? 0.8));
    k.metal({ f: 740, dur: 0.6, vol: 0.12, partials: BELL });
    k.metal({ f: 1047, at: 0.09, dur: 0.5, vol: 0.1, partials: BELL });
    k.tone({ type: 'square', f: 880, at: 0, dur: 0.07, vol: 0.035 });
    k.tone({ type: 'square', f: 1245, at: 0.09, dur: 0.07, vol: 0.035 });
    k.noise({ dur: len, vol: 0.2, attack: len * 0.9, f: 500, f1: 3400, q: 5, linear: true });
    k.tone({
      type: 'sawtooth',
      f: 120,
      f1: 240,
      dur: len,
      vol: 0.05,
      attack: len * 0.8,
      out: k.filter('lowpass', 900),
    });
    drum(k, { f: 70, vol: 0.3 });
  },
);

registerSfx('combat.telegraph', { label: 'วงอันตรายบนพื้น', cat: 'combat', vary: 0.02, gap: 0.2 }, (k, o) => {
  // A ground blast marked: a low pulsing hum that tightens toward the blast.
  const len = Math.max(0.5, Math.min(2.5, o.len ?? 0.9));
  k.tone({ f: 98, dur: len, vol: 0.14, attack: 0.05, hold: len * 0.7, vib: [6, 0.02] });
  k.noise({ color: 'pink', dur: len, vol: 0.08, attack: len * 0.8, f: 300, f1: 900, q: 3, trem: [8, 0.8] });
});

registerSfx('combat.charge', { label: 'รวมลมปราณ', cat: 'combat', vary: 0.04, gap: 0.1 }, (k, o) => {
  const len = Math.max(0.35, Math.min(1.6, o.len ?? 0.5));
  k.tone({ f: 200, f1: 620, dur: len, vol: 0.06, attack: len * 0.7 });
  k.noise({ dur: len, vol: 0.1, attack: len * 0.8, f: 300, f1: 1700, q: 4 });
  k.tone({ f: 1200, f1: 1800, dur: len, vol: 0.012, attack: len * 0.9, vib: [9, 0.02] });
});

registerSfx(
  'combat.ultimate.charge',
  { label: 'ท่าใหญ่: รวมพลัง', cat: 'combat', vary: 0.02, gap: 0.3, preview: 1.6 },
  (k, o) => {
    // Rage coiling: a deep drone opening up, a heartbeat, shimmering partials rising.
    const len = Math.max(0.6, Math.min(2.5, o.len ?? 1.2));
    const lp = k.filter('lowpass', 200, 3);
    lp.frequency.setValueAtTime(200, k.t);
    lp.frequency.exponentialRampToValueAtTime(2400, k.t + len);
    k.tone({ type: 'sawtooth', f: 55, f1: 110, dur: len + 0.1, vol: 0.18, attack: len * 0.6, out: lp });
    k.tone({ type: 'sawtooth', f: 55.6, f1: 111, dur: len + 0.1, vol: 0.14, attack: len * 0.6, out: lp });
    for (const b of [0, len * 0.45, len * 0.8]) k.thump({ f0: 80, f1: 40, at: b, dur: 0.2, vol: 0.3 });
    for (let n = 0; n < 4; n++)
      k.tone({
        f: note(440, n * 2),
        f1: note(440, n * 2 + 3),
        at: len * 0.2,
        dur: len * 0.85,
        vol: 0.02,
        attack: len * 0.6,
      });
    k.noise({ dur: len, vol: 0.12, attack: len * 0.95, f: 400, f1: 4000, q: 2, linear: true });
  },
);

registerSfx(
  'combat.ultimate.release',
  { label: 'ท่าใหญ่: ปลดปล่อย', cat: 'combat', vary: 0.02, gap: 0.3, preview: 3 },
  (k) => {
    const crunch = k.shaper(5, k.gain(0.6));
    k.thump({ f0: 75, f1: 24, dur: 0.9, vol: 0.6 });
    k.noise({
      color: 'brown',
      dur: 1.1,
      vol: 0.55,
      attack: 0.003,
      type: 'lowpass',
      f: 3500,
      f1: 90,
      out: crunch,
    });
    k.noise({ dur: 0.5, vol: 0.25, attack: 0.002, type: 'highpass', f: 2500, f1: 600 });
    k.metal({ f: 410, dur: 1.4, vol: 0.12 });
    gong(k, { f: 98, at: 0.04, dur: 2.6, vol: 0.12 });
  },
);

registerSfx('combat.boom', { label: 'ระเบิดพื้น', cat: 'combat', vary: 0.05, gap: 0.1 }, (k) => {
  k.thump({ f0: 115, f1: 30, dur: 0.7, vol: 0.5 });
  k.noise({ color: 'brown', dur: 0.75, vol: 0.55, attack: 0.003, type: 'lowpass', f: 900, f1: 60 });
  k.noise({ color: 'crackle', dur: 0.5, vol: 0.5, type: 'highpass', f: 1500 });
});

// ───────── states ─────────

registerSfx('combat.stun', { label: 'มึนงง', cat: 'combat', vary: 0.05, gap: 0.3, preview: 1.1 }, (k) => {
  // Seeing stars: a ringing in the ears and little birdlike chirps circling.
  k.tone({ f: 2600, dur: 0.9, vol: 0.025, attack: 0.02, vib: [7, 0.01] });
  k.thump({ f0: 300, f1: 120, dur: 0.12, vol: 0.2 });
  for (let n = 0; n < 4; n++) {
    const at = 0.1 + n * 0.17;
    k.tone({ f: 1900 + (n % 2) * 400, f1: 2900 + (n % 2) * 300, at, dur: 0.09, vol: 0.05, attack: 0.01 });
  }
});

registerSfx('combat.death', { label: 'ศัตรูล้ม', cat: 'combat', vary: 0.08, gap: 0.1 }, (k) => {
  k.thump({ f0: 95, f1: 38, at: 0.08, dur: 0.35, vol: 0.4 });
  k.noise({ color: 'brown', at: 0.06, dur: 0.6, vol: 0.4, attack: 0.02, type: 'lowpass', f: 700, f1: 120 });
  k.noise({ at: 0.1, dur: 0.5, vol: 0.06, attack: 0.1, f: 2000, f1: 900, q: 0.8 });
});

registerSfx(
  'combat.death.player',
  { label: 'ผู้เล่นสิ้นใจ', cat: 'combat', vary: 0, gap: 1, preview: 3 },
  (k) => {
    // A low toll and everything falling away.
    gong(k, { f: 73.4, dur: 2.8, vol: 0.14 });
    k.tone({ f: 440, f1: 90, dur: 1.6, vol: 0.06, attack: 0.05 });
    k.tone({ f: 293.66, f1: 60, at: 0.1, dur: 1.8, vol: 0.05, attack: 0.05 });
    k.thump({ f0: 90, f1: 35, at: 0.25, dur: 0.4, vol: 0.35 });
  },
);

registerSfx('combat.death.boss', { label: 'ทลายบอส', cat: 'combat', vary: 0, gap: 1, preview: 3.5 }, (k) => {
  k.thump({ f0: 90, f1: 24, dur: 1.2, vol: 0.6 });
  k.noise({ color: 'brown', dur: 2.2, vol: 0.5, attack: 0.003, type: 'lowpass', f: 1800, f1: 70 });
  k.noise({ color: 'crackle', dur: 1.2, vol: 0.6, type: 'highpass', f: 1200 });
  gong(k, { f: 82.4, at: 0.15, dur: 3.2, vol: 0.16 });
});

registerSfx('combat.respawn', { label: 'ฟื้นคืนชีพ', cat: 'combat', vary: 0, gap: 1, preview: 2 }, (k) => {
  // Breath coming back: an airy swell and a rising pentatonic shimmer.
  k.noise({ color: 'pink', dur: 1.3, vol: 0.12, attack: 0.6, f: 500, f1: 3000, q: 1.2 });
  for (let n = 0; n < 6; n++)
    k.pluck({ f: note(ROOT_D, n + 2), at: 0.15 + n * 0.09, dur: 1.3, vol: 0.12, bright: 0.7 });
  k.tone({ f: note(ROOT_D, 7), at: 0.6, dur: 1.2, vol: 0.03, attack: 0.3 });
});

// ───────── the weapon itself ─────────

registerSfx('combat.draw', { label: 'ชักกระบี่', cat: 'combat', vary: 0.05, gap: 0.2 }, (k) => {
  // Shiiing: steel sliding out of the scabbard, ringing as it clears.
  k.noise({ dur: 0.32, vol: 0.18, attack: 0.2, f: 1600, f1: 6200, q: 7 });
  k.noise({ dur: 0.3, vol: 0.06, attack: 0.15, type: 'highpass', f: 6000 });
  ring(k, { f: 3150, at: 0.26, dur: 0.6, vol: 0.05 });
});

registerSfx('combat.sheathe', { label: 'เก็บกระบี่เข้าฝัก', cat: 'combat', vary: 0.05, gap: 0.2 }, (k) => {
  k.noise({ dur: 0.22, vol: 0.14, attack: 0.05, f: 4200, f1: 1500, q: 6 });
  k.click({ f: 1800, at: 0.22, vol: 0.3, q: 4 });
  k.thump({ f0: 300, f1: 160, at: 0.22, dur: 0.06, vol: 0.18 });
});

registerSfx('combat.ching', { label: 'ประกายคมกระบี่', cat: 'combat', vary: 0.06, gap: 0.05 }, (k) => {
  k.metal({ f: 2400, dur: 0.45, vol: 0.06 });
  k.noise({ dur: 0.05, vol: 0.06, type: 'highpass', f: 6000 });
});

registerSfx('combat.pk', { label: 'เปิดแดง (PK)', cat: 'combat', vary: 0, gap: 0.5, preview: 1.8 }, (k) => {
  // Blood up: a war drum and a dissonant low string.
  drum(k, { f: 62, vol: 0.45, dur: 0.6 });
  drum(k, { f: 62, at: 0.28, vol: 0.35, dur: 0.6 });
  k.pluck({ f: 98, at: 0.05, dur: 1.5, vol: 0.2, bright: 0.8 });
  k.pluck({ f: 138.6, at: 0.07, dur: 1.4, vol: 0.14, bright: 0.8 });
});
